/**
 * Axios instance for the admin frontend.
 *
 * - baseURL  : API_V1_URL  (http://localhost:8000/api/v1)
 * - Auth     : reads "admin_token" from localStorage (separate key
 *              from the user frontend's "token" to avoid collisions)
 * - 401 hook : clears admin_token + admin_user, redirects to /login
 */
import axios from 'axios';
import { API_V1_URL } from '../config/api.config';

const api = axios.create({
  baseURL: API_V1_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach admin JWT ─────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: handle token expiry ────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url ?? '';
    const isAuthCall = url.includes('/auth/login');
    if (error.response?.status === 401 && !isAuthCall) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe:  ()            => api.get('/auth/me'),
};

// ── Admin — Stats ─────────────────────────────────────────────────
export const statsAPI = {
  getSubmissions: (params) => api.get('/admin/stats/submissions', { params }),
  getLLMUsage:    ()        => api.get('/admin/stats/llm-usage'),
};

// ── Admin — Users ─────────────────────────────────────────────────
export const usersAPI = {
  list:         (params)     => api.get('/admin/users', { params }),
  getById:      (id)         => api.get(`/admin/users/${id}`),
  update:       (id, data)   => api.patch(`/admin/users/${id}`, data),
  toggleActive: (id)         => api.patch(`/admin/users/${id}/toggle-active`),
  delete:       (id)         => api.delete(`/admin/users/${id}`),
};

// ── Admin — Orgs ──────────────────────────────────────────────────
export const orgsAPI = {
  list:          ()           => api.get('/admin/orgs'),
  getById:       (id)         => api.get(`/admin/orgs/${id}`),
  create:        (data)       => api.post('/admin/orgs', data),
  update:        (id, data)   => api.patch(`/admin/orgs/${id}`, data),
  generateCodes: (id, data)   => api.post(`/admin/orgs/${id}/codes`, data),
  listCodes:     (id, params) => api.get(`/admin/orgs/${id}/codes`, { params }),
  deleteCode:    (orgId, codeId) => api.delete(`/admin/orgs/${orgId}/codes/${codeId}`),
  addCredits:    (id, data)   => api.post(`/admin/orgs/${id}/credits`, data),
  getUsage:      (id)         => api.get(`/admin/orgs/${id}/usage`),
};

// ── Admin — Projects ──────────────────────────────────────────────
export const projectsAPI = {
  list:           (params)        => api.get('/admin/projects', { params }),
  getById:        (id)            => api.get(`/admin/projects/${id}`),
  create:         (data)          => api.post('/admin/projects', data),
  update:         (id, data)      => api.patch(`/admin/projects/${id}`, data),
  deactivate:     (id)            => api.delete(`/admin/projects/${id}`),
  addStage:       (projId, data)  => api.post(`/admin/projects/${projId}/stages`, data),
  updateStage:    (stageId, data) => api.patch(`/admin/stages/${stageId}`, data),
  deleteStage:    (stageId)       => api.delete(`/admin/stages/${stageId}`),
  getPendingReviews: (params)     => api.get('/admin/stage-reviews', { params }),
  reviewSubmission:  (id, data)   => api.patch(`/admin/stage-reviews/${id}`, data),
};

// ── Admin — Pricing ───────────────────────────────────────────────────────
export const pricingAPI = {
  getAll:          ()     => api.get('/admin/pricing/pricing-configs'),
  updateLearning:  (data) => api.put('/admin/pricing/learning', data),
  updateInternship:(data) => api.put('/admin/pricing/internship', data),
  updateCurrency:  (data) => api.put('/admin/pricing/currency', data),
};

// ── Admin — Credit Packs ─────────────────────────────────────────────────
export const creditPacksAPI = {
  getAll:  ()           => api.get('/admin/credit-packs'),
  create:  (data)       => api.post('/admin/credit-packs', data),
  update:  (id, data)   => api.put(`/admin/credit-packs/${id}`, data),
  toggle:  (id)         => api.patch(`/admin/credit-packs/${id}/toggle`),
  remove:  (id)         => api.delete(`/admin/credit-packs/${id}`),
};

// ── Admin — Submissions ───────────────────────────────────────────
export const submissionsAPI = {
  list:    (params) => api.get('/admin/submissions', { params }),
  getById: (id)     => api.get(`/submissions/${id}`),
};

// ── Admin — Enrollments ───────────────────────────────────────────
export const enrollmentsAPI = {
  list:     (params) => api.get('/admin/enrollments', {
    params,
    // FastAPI expects repeated keys for lists: project_id=1&project_id=2
    paramsSerializer: (p) => {
      const parts = [];
      Object.entries(p).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          val.forEach(v => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
        } else if (val !== undefined && val !== null && val !== '') {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
        }
      });
      return parts.join('&');
    },
  }),
  stats:    ()       => api.get('/admin/enrollments/stats'),
  projects: ()       => api.get('/admin/enrollments/projects'),
};

// ── Admin — Team Management ───────────────────────────────────────
export const teamAPI = {
  list:       ()              => api.get('/admin/team'),
  create:     (data)          => api.post('/admin/team/create', data),
  changeRole: (userId, role)  => api.patch(`/admin/team/${userId}/role`, { admin_role: role }),
  update:     (userId, data)  => api.patch(`/admin/team/${userId}`, data),
  remove:     (userId)        => api.delete(`/admin/team/${userId}`),
};

export default api;
